/**
 * Creating a project, one question at a time, while explaining the conventions.
 *
 * WHY THIS EXISTS ALONGSIDE ProjectEditor, WHICH ALREADY CREATES PROJECTS
 * ----------------------------------------------------------------------
 * The editor is a form: it assumes you know what a lane is, what a group is for, that
 * phases are seeded from a tick-box, and that "Coding: 2FA delivery" draws coral while
 * "2FA delivery" draws driftwood. Everybody who has used the board for a month knows
 * all four. Nobody knows any of them on their first morning, and a form cannot teach
 * them without becoming a worse form.
 *
 * So this is the same create, slowed down and annotated, and the editor is left alone.
 * It is NOT a replacement: after the first time, the form is faster and should be.
 *
 * IT TEACHES BY LETTING YOU DO IT, NOT BY DESCRIBING IT
 * ----------------------------------------------------
 * The prefix step is the clearest case. It could say "name a phase Coding: something
 * and it draws as coding" - instead it shows the swatch the name you typed will
 * actually produce, so renaming a row to "Coding: API" turns it coral while you watch.
 * That is a fact about the app demonstrated rather than asserted, and it survives the
 * reader forgetting this dialog ever happened.
 *
 * The dates step is the same idea: the bar is drawn from what has been typed, so a
 * phase with one date shows what one date gets you, which is nothing.
 *
 * NOTHING IS WRITTEN UNTIL THE LAST STEP. One POST creates the lane, its phases and
 * their dates together. Creating the project early and adding phases as the reader
 * went would leave a half-built lane on the roadmap every time somebody closed the
 * dialog to think about it - and the roadmap is a shared surface, so an abandoned
 * draft is everybody's problem.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  createProject,
  describeError,
  getCapexPhases,
  getPeople,
  getRoadmap,
} from '../services/api';
import {
  CAPEX_STYLE,
  displayHeading,
  palette,
  radius,
  shadow,
  STATE_STYLE,
} from '../styles/theme';
import {
  ErrorText,
  Hint,
  Input,
  Label,
  PrimaryButton,
  SecondaryButton,
  Select,
} from '../styles/ui';
import type { CapexPhase, PhaseCreate, Person, Project } from '../types';
import { capitalNames, normalise } from '../utils/capex';
import { phaseState } from '../utils/phaseState';
import { STANDARD_PHASES } from './ProjectEditor';

/* ------------------------------------------------------------------ the shell */

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(29, 36, 81, 0.45);
  overflow-y: auto;
`;

const Card = styled.div`
  width: 100%;
  max-width: 640px;
  margin: auto;
  border: 2px solid ${palette.borderStrong};
  border-radius: ${radius.md};
  background: ${palette.cardSolid};
  box-shadow: ${shadow.card};
  padding: 22px 24px 18px;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
`;

const Title = styled.h2`
  ${displayHeading};
  font-size: 17px;
  color: ${palette.deepMagenta};
  margin: 0;
`;

const Count = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${palette.inkSoft};
`;

/* A real button with an accessible name: an × alone announces as "times". */
const Close = styled.button`
  flex: none;
  border: 0;
  background: none;
  padding: 0 0 0 4px;
  font: inherit;
  font-size: 15px;
  line-height: 1;
  color: ${palette.inkSoft};
  cursor: pointer;

  &:hover {
    color: ${palette.danger};
  }
`;

const Body = styled.div`
  font-size: 13.5px;
  line-height: 1.5;
  color: ${palette.ink};

  p {
    margin: 0 0 12px;
  }
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 14px;
  margin: 14px 0 4px;
`;

const Wide = styled.div`
  grid-column: 1 / -1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
`;

const Spacer = styled.span`
  flex: 1;
`;

const Dots = styled.div`
  display: flex;
  gap: 5px;
`;

const Dot = styled.span<{ $on: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => (p.$on ? palette.deepMagenta : palette.hairlineStrong)};
`;

/* ------------------------------------------------------------- the phase rows */

const PhaseRows = styled.div`
  display: grid;
  gap: 6px;
  margin: 12px 0 6px;
`;

const PhaseRow = styled.div`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 8px;
`;

/* The whole point of the step: the colour this name will actually draw. */
const Swatch = styled.span<{ $fill: string }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${(p) => p.$fill};
  border: 1px solid rgba(20, 26, 59, 0.25);
`;

/*
  Whether this phase would be capitalised, marked on the row rather than grouped.

  The six presets are in the order the work HAPPENS - planning, then wireframes, then
  architecting, coding, testing - and that order is why the list is readable at all.
  Splitting it into "capital" and "not capital" groups would scramble a sequence
  everybody already knows in order to answer a question most people building a lane
  are not asking. So the answer rides on the row instead, next to the colour swatch
  that is already there for the same reason.

  It is a statement about the PHASE NAME only. Whether any of it is actually
  capitalised depends on the project being classified capex, which happens later and
  by somebody else - hence "would be", and hence the quiet styling: this is a hint on
  a form, not a figure in a report.
*/
const CapitalMark = styled.span<{ $capital: boolean }>`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  padding: 2px 6px;
  border-radius: ${radius.pill};
  color: ${(p) => (p.$capital ? palette.onAccent : palette.inkSoft)};
  background: ${(p) => (p.$capital ? CAPEX_STYLE.capital.fill : 'transparent')};
  border: 1px solid
    ${(p) => (p.$capital ? CAPEX_STYLE.capital.fill : palette.border)};
`;

const StateName = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${palette.inkSoft};
  white-space: nowrap;
`;

const Drop = styled.button`
  border: 0;
  background: none;
  padding: 0 2px;
  font: inherit;
  font-size: 14px;
  line-height: 1;
  color: ${palette.inkSoft};
  cursor: pointer;

  &:hover {
    color: ${palette.danger};
  }
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 8px;
`;

const PhaseName = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${palette.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Verdict = styled.span<{ $warn?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.$warn ? palette.danger : palette.inkSoft)};
`;

const Review = styled.dl`
  margin: 12px 0 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 4px 12px;
  font-size: 12.5px;

  dt {
    font-weight: 700;
    color: ${palette.inkSoft};
  }

  dd {
    margin: 0;
    color: ${palette.ink};
  }
`;

/* ------------------------------------------------------------------- the data */

/**
 * A phase being drafted. Dates are strings because that is what a date input gives
 * back, and '' is the null - see PhaseEditor, which makes the same point at length.
 */
interface Draft {
  /* Stable across renames, so React does not reuse the wrong input when a row goes. */
  key: string;
  name: string;
  structural: boolean;
  start: string;
  end: string;
}

let nextKey = 0;
function draftKey(): string {
  nextKey += 1;
  return `draft-${nextKey}`;
}

function startingDrafts(): Draft[] {
  return STANDARD_PHASES.map((phase) => ({
    key: draftKey(),
    name: phase.name,
    structural: phase.structural,
    start: '',
    end: '',
  }));
}

/**
 * What a drafted phase will contribute to the create.
 *
 * Blank dates are dropped rather than sent as '', for the reason PhaseEditor gives:
 * the API must receive a real null, and an omitted field takes the honest default.
 * A structural phase sends no dates at all - it is an ongoing band, and a date on one
 * is a number nobody will ever update.
 */
export function toPhaseCreate(drafts: Draft[]): PhaseCreate[] {
  return drafts
    .filter((d) => d.name.trim())
    .map((d, index) => {
      const phase: PhaseCreate = {
        name: d.name.trim(),
        phase_order: index,
        structural: d.structural,
      };
      if (!d.structural && d.start) {
        phase.start = d.start;
      }
      if (!d.structural && d.end) {
        phase.end = d.end;
      }
      return phase;
    });
}

/**
 * What to say about a drafted phase's dates.
 *
 * Three states, and the middle one is why this exists: a phase with ONE date draws
 * nothing, which is the surprise worth heading off while somebody is still typing.
 * Returning null for a structural row keeps "Maintenance" from being reported as two
 * missing fields on a lane that is one minute old.
 */
export function dateVerdict(draft: Draft): { text: string; warn: boolean } | null {
  if (draft.structural) {
    return null;
  }
  if (!draft.start && !draft.end) {
    return { text: 'not scheduled', warn: false };
  }
  if (!draft.start || !draft.end) {
    return { text: 'needs both to draw', warn: true };
  }
  if (draft.end < draft.start) {
    return { text: 'ends before it starts', warn: true };
  }
  return { text: 'on the chart', warn: false };
}

/** Every date pair that is filled in has to make sense before the create is offered. */
export function datesAreSane(drafts: Draft[]): boolean {
  return drafts.every((d) => {
    const verdict = dateVerdict(d);
    return !verdict?.warn;
  });
}

/* ---------------------------------------------------------------- the component */

export interface ProjectWizardProps {
  /** Called when the wizard is finished OR dismissed. */
  onDone: () => void;
  /**
   * Whether to open on the "want to create a project?" question.
   *
   * True when the wizard arrives uninvited, after the tour - somebody who has just
   * been shown round has not asked for anything and deserves a door rather than a
   * form. False when they pressed a button that said "create a project", where asking
   * again would be a dialog confirming the click that opened it.
   */
  ask?: boolean;
}

const STEP_TITLES = ['Name it', 'Phases', 'Dates', 'Ready'];

export default function ProjectWizard({ onDone, ask = false }: ProjectWizardProps) {
  const navigate = useNavigate();

  /* -1 is the question; 0..3 are the steps. */
  const [step, setStep] = useState(ask ? -1 : 0);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [dri, setDri] = useState('');
  const [drafts, setDrafts] = useState<Draft[]>(startingDrafts);

  const [people, setPeople] = useState<Person[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  /*
    The capitalisation policy, for the mark on each phase row.

    FETCHED, not written here, even though a six-item list would be trivial to inline.
    The policy lives in fast/app/capex.py and is served by /api/finance/capex-phases;
    a copy in this file would be a second place that has to be edited when finance
    changes what counts, and the one that nobody would remember.

    Empty on failure, and the mark simply does not draw - see the catch below. A
    wizard that refused to open because an unrelated endpoint was down would be a
    worse trade than a missing hint.
  */
  const [policy, setPolicy] = useState<CapexPhase[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    The roster and the existing lanes, for the DRI picker and the group suggestions.

    Fetched here rather than taken as props because this opens from three places -
    after the tour, from + Add, and from the guides - and only one of them has either
    list already. Failing quietly is right: the picker falls back to an empty roster
    and a free-text group, which still creates a perfectly good project.
  */
  useEffect(() => {
    let live = true;
    void Promise.all([getPeople(), getRoadmap(), getCapexPhases()])
      .then(([roster, roadmap, phases]) => {
        if (live) {
          setPeople(roster);
          setProjects(roadmap.projects);
          setPolicy(phases);
        }
      })
      .catch(() => {
        /* Left empty. See above. */
      });
    return () => {
      live = false;
    };
  }, []);

  /* The capitalisable names as a set, built once rather than per phase row. */
  const capital = useMemo(() => capitalNames(policy), [policy]);

  /* Every group already in use, so the second project reuses the first's spelling. */
  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const project of projects) {
      const value = (project.category ?? '').trim();
      if (value) {
        seen.add(value);
      }
    }
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [projects]);

  /* Last on the roadmap. A new lane arriving in the middle of somebody's chart would
     be a surprise; arriving at the bottom is a new row they can move. */
  const laneOrder = useMemo(
    () => projects.reduce((max, p) => Math.max(max, p.lane_order), -1) + 1,
    [projects]
  );

  const named = name.trim().length > 0;
  const kept = drafts.filter((d) => d.name.trim());

  const setDraft = (key: string, patch: Partial<Draft>) =>
    setDrafts((current) => current.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const create = async () => {
    setError(null);
    setSaving(true);
    try {
      const project = await createProject({
        name: name.trim(),
        lane_order: laneOrder,
        category: category.trim() || null,
        dri_email: dri.trim().toLowerCase() || null,
        phases: toPhaseCreate(drafts),
      });
      onDone();
      /* Straight to the lane they just made, rather than a success message about it. */
      navigate(`/?project=${encodeURIComponent(project.project_id)}`);
    } catch (e) {
      setError(describeError(e));
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------- the question */

  if (step === -1) {
    return (
      <Scrim role="dialog" aria-modal="true" aria-label="Create a project">
        <Card>
          <Head>
            <Title>Want to create a project?</Title>
            <Close type="button" onClick={onDone} aria-label="Close">
              ✕
            </Close>
          </Head>
          <Body>
            <p>
              It takes about a minute, and it is the fastest way to learn how the
              roadmap is put together — the groups, the phases, the naming convention
              that decides what colour a bar draws, and the dates.
            </p>
            <p>
              Nothing is saved until the last step, and you can delete a project you
              created yourself afterwards. So there is nothing to break.
            </p>
          </Body>
          <Actions>
            <Spacer />
            <SecondaryButton type="button" $compact onClick={onDone}>
              Not now
            </SecondaryButton>
            <PrimaryButton type="button" $compact onClick={() => setStep(0)}>
              Yes, walk me through it
            </PrimaryButton>
          </Actions>
        </Card>
      </Scrim>
    );
  }

  /* ----------------------------------------------------------------- the steps */

  const stepBody = () => {
    if (step === 0) {
      return (
        <>
          <p>
            A project is one lane across the chart. Give it the name people already
            call it — the chart has room for about thirty characters before it starts
            truncating, and the full name shows on hover.
          </p>
          <Fields>
            <Wide>
              <Label>
                Name
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Client Portal"
                  autoFocus
                />
              </Label>
            </Wide>
            <Label>
              Group
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                list="wizard-groups"
                placeholder="App"
              />
              {/* Existing values offered rather than enumerated, which is what keeps
                  "Data" from becoming "data" and "DATA". Typing a new one is fine. */}
              <datalist id="wizard-groups">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <Hint>The heading it sits under. Leave it blank to file it later.</Hint>
            </Label>
            <Label>
              DRI
              <Select value={dri} onChange={(e) => setDri(e.target.value)}>
                <option value="">Unassigned</option>
                {people.map((person) => (
                  <option key={person.email} value={person.email}>
                    {person.name || person.email}
                  </option>
                ))}
              </Select>
              <Hint>Who answers for it. Unassigned is a real answer.</Hint>
            </Label>
          </Fields>
        </>
      );
    }

    if (step === 1) {
      return (
        <>
          <p>
            Phases are the bars inside the lane. These six are what most projects
            carry — rename them, drop the ones that do not apply, add your own.
          </p>
          <p>
            <strong>The name decides the colour.</strong> A phase called{' '}
            <code>Coding</code> draws coral; so does <code>Coding: 2FA delivery</code>,
            because the app reads the word before the colon. Anything it does not
            recognise draws driftwood, which is honest rather than a guess.
          </p>
          <p>
            {/* Said here rather than left to the badges alone, because "Capital" on a
                row somebody is naming reads as a decision they are making, and it is
                not - it is a consequence of the name, conditional on a classification
                that happens elsewhere and later. */}
            <strong>The name also decides capitalisation.</strong> Building work —
            wireframes, architecting, coding, testing — counts as capital;{' '}
            <code>Planning</code> and ongoing support never do. The badge says what a
            phase <em>would</em> be: none of it is capitalised until somebody classifies
            the project itself as capex, which is done from the project editor.
          </p>
          <PhaseRows>
            {drafts.map((draft) => {
              const state = phaseState(draft.name);
              const style = STATE_STYLE[state];
              /*
                Structural phases are excluded outright, the same as on the server -
                ongoing support does not build an asset whatever it is named, which is
                why Maintenance is marked even though its NAME is also off the list.
              */
              const wouldCapitalise =
                !draft.structural && capital.has(normalise(draft.name));
              return (
                <PhaseRow key={draft.key}>
                  <Swatch $fill={style.fill} aria-hidden />
                  <Input
                    value={draft.name}
                    onChange={(e) => setDraft(draft.key, { name: e.target.value })}
                    aria-label="Phase name"
                  />
                  <StateName>{style.label}</StateName>
                  {policy.length > 0 ? (
                    <CapitalMark
                      $capital={wouldCapitalise}
                      title={
                        wouldCapitalise
                          ? 'Counts as capital work, if this project is classified capex'
                          : 'Never capitalised, whatever the project is classified'
                      }
                    >
                      {wouldCapitalise ? 'Capital' : 'Expense'}
                    </CapitalMark>
                  ) : null}
                  <Drop
                    type="button"
                    onClick={() =>
                      setDrafts((current) => current.filter((d) => d.key !== draft.key))
                    }
                    aria-label={`Remove ${draft.name || 'this phase'}`}
                  >
                    ✕
                  </Drop>
                </PhaseRow>
              );
            })}
          </PhaseRows>
          <SecondaryButton
            type="button"
            $compact
            onClick={() =>
              setDrafts((current) => [
                ...current,
                { key: draftKey(), name: '', structural: false, start: '', end: '' },
              ])
            }
          >
            Add a phase
          </SecondaryButton>
          <Hint>
            {' '}
            Try typing <code>Coding: </code> in front of one and watch the swatch.
          </Hint>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <p>
            Dates are optional, and a phase needs <strong>both</strong> to draw a bar —
            one date on its own puts nothing on the chart, which is the thing that
            surprises people.
          </p>
          <p>
            Leave the ones you do not know blank. An honest gap is better than a date
            invented to fill the field, and progress can be recorded on a phase with no
            dates at all.
          </p>
          <PhaseRows>
            {kept.map((draft) => {
              const verdict = dateVerdict(draft);
              return (
                <DateRow key={draft.key}>
                  <PhaseName>{draft.name}</PhaseName>
                  {draft.structural ? (
                    <Verdict>ongoing — no dates</Verdict>
                  ) : (
                    <>
                      <Input
                        type="date"
                        value={draft.start}
                        onChange={(e) => setDraft(draft.key, { start: e.target.value })}
                        aria-label={`${draft.name} starts`}
                      />
                      <Input
                        type="date"
                        value={draft.end}
                        onChange={(e) => setDraft(draft.key, { end: e.target.value })}
                        aria-label={`${draft.name} ends`}
                      />
                    </>
                  )}
                  {verdict ? (
                    <Verdict $warn={verdict.warn}>{verdict.text}</Verdict>
                  ) : null}
                </DateRow>
              );
            })}
          </PhaseRows>
        </>
      );
    }

    const scheduled = kept.filter((d) => !d.structural && d.start && d.end).length;
    return (
      <>
        <p>
          That is the whole thing. Creating it puts the lane at the bottom of the
          roadmap, where you can drag it into place.
        </p>
        <Review>
          <dt>Name</dt>
          <dd>{name.trim()}</dd>
          <dt>Group</dt>
          <dd>{category.trim() || 'not filed'}</dd>
          <dt>DRI</dt>
          <dd>{dri ? people.find((p) => p.email === dri)?.name || dri : 'unassigned'}</dd>
          <dt>Phases</dt>
          <dd>
            {kept.length} — {scheduled} with dates
          </dd>
        </Review>
        <Hint>
          You created it, so you can archive or delete it yourself under Settings →
          Projects. No admin needed.
        </Hint>
      </>
    );
  };

  const canGoOn = step === 0 ? named : step === 2 ? datesAreSane(drafts) : true;

  /* Why Next is disabled, said on the control itself. A greyed button with no
     explanation is the reader guessing at a rule the app already knows. */
  const blocked =
    step === 0 ? 'Give it a name first.' : 'Fix the rows marked in red first.';

  return (
    <Scrim role="dialog" aria-modal="true" aria-label="Create a project">
      <Card>
        <Head>
          <Title>{STEP_TITLES[step]}</Title>
          <Count>
            {step + 1} of {STEP_TITLES.length}
          </Count>
          <Close type="button" onClick={onDone} aria-label="Close">
            ✕
          </Close>
        </Head>

        <Body>{stepBody()}</Body>

        {error ? <ErrorText role="alert">{error}</ErrorText> : null}

        <Actions>
          <Dots aria-hidden>
            {STEP_TITLES.map((title, i) => (
              <Dot key={title} $on={i <= step} />
            ))}
          </Dots>
          <Spacer />
          {step > 0 ? (
            <SecondaryButton type="button" $compact onClick={() => setStep(step - 1)}>
              Back
            </SecondaryButton>
          ) : null}
          {step === STEP_TITLES.length - 1 ? (
            <PrimaryButton type="button" $compact disabled={saving} onClick={() => void create()}>
              {saving ? 'Creating…' : 'Create the project'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              type="button"
              $compact
              disabled={!canGoOn}
              title={canGoOn ? undefined : blocked}
              onClick={() => setStep(step + 1)}
            >
              Next
            </PrimaryButton>
          )}
        </Actions>
      </Card>
    </Scrim>
  );
}
